import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Award, Upload, Download, RefreshCw, Loader2, AlertCircle, Save, ArrowUp, ArrowDown, Search, X, Plus, ChevronDown, ChevronRight, FileSpreadsheet, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import type { LinkedSearch } from '../hooks/useLinkedSearch';
import { appleScorecardAPI, tasksAPI } from '../services/api';
import Modal from '../components/Modal';
import { ActiveStatusPill } from '../components/ActiveStatusPill';
import { InlineLoader } from '../components/loading';
import PeriodMonthPicker from '../components/PeriodMonthPicker';
import { getImageViewUrl } from '../utils/imageUrl';
import { fileToBase64 } from '../utils/fileToBase64';

const APPLE_PROJECT_ID = 170;

type TabKey = 'scorecard' | 'trainings' | 'training_attendance' | 'seed_scores' | 'slabs';

interface SeedScoreSubmissionRow {
  employee_code: string;
  employee_name: string | null;
  date_of_joining: string | null;
  store_name: string | null;
  store_code: string | null;
  store_address: string | null;
  answer_id: string | null;
  photo_url: string | null;
  extracted_data: { value?: number | string; raw_text?: string; source?: string; admin_overridden_at?: string } | null;
  extracted_at: string | null;
  submitted_at: string | null;
  aggregate_value: string | number | null;  // pg returns NUMERIC as string
}

interface ParameterScore { value: number; points: number }

interface EmployeeScorecard {
  employee_code: string;
  employee_name: string;
  store_name: string;
  store_code: string;
  store_category: string;
  store_trade: string;
  am_feedback: ParameterScore & { sample_count: number };
  seed_score: ParameterScore;
  grooming: ParameterScore;
  random: ParameterScore & { slots_received: number; slots_marked: number };
  leave_policy: ParameterScore;
  on_time: ParameterScore;
  training: ParameterScore & { scheduled: number; attended: number };
  total: number;
}

interface SlabRow {
  parameter: string;
  lower_bound: number | null;
  upper_bound: number | null;
  points: number;
  display_label: string;
  display_order: number;
}

interface TrainingRow {
  id: string;
  training_code?: string | null;
  name: string;
  training_datetime: string;
  training_type: 'in_person' | 'web_based';
  description: string | null;
  registration_url: string | null;
  duration_minutes: number | null;
  status: 'active' | 'cancelled';
  scheduled_count: number;
  attended_count: number;
  created_at?: string;
  updated_at?: string;
  /** Set when this training was created by an onboarding batch (not a normal one). */
  onboarding_batch_id?: number | null;
  /**
   * Sign-out check: true (On, the default) = people not marked Registered in time cannot
   * end their day; false = this training blocks nobody. Absent from an older server = On.
   */
  signout_check?: boolean;
  signout_check_changed_at?: string | null;
  signout_check_changed_by?: string | null;
}

interface AttendeeRow {
  id: string;
  employee_code: string;
  employee_name?: string | null;
  // Tri-state: null/undefined = unselected, true = Yes, false = No.
  attended: boolean | null;
  attendance_marked_at?: string | null;
  attendance_source?: string | null;
  registered?: boolean | null;
  registered_at?: string | null;
  registration_screenshot_url?: string | null;
}

interface EmployeeOption {
  employee_code: string;
  employee_name?: string;
  city?: string;
  area_manager?: string;
  store_category?: string;
  store_trade?: string;
  budget_id?: string;
  budget_name?: string;
  designation?: string;
  // false → not deployed at a store (Area Manager / Trade Head / etc.)
  is_store?: boolean;
}

const todayMonthInput = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthInputToPeriod = (value: string): string => `${value}-01`;

const formatIST = (iso: string): string => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

/**
 * Can this training's Sign-out check matter? Only for an active, normal training that has
 * not started: the End Day block never applies to cancelled trainings, onboarding sessions
 * or a training once it starts, so the server refuses the switch for them too.
 */
const signoutCheckApplies = (t: TrainingRow): boolean =>
  t.status === 'active' && t.onboarding_batch_id == null && new Date(t.training_datetime).getTime() > Date.now();

/** Hover text for the Sign-out check cell: what it does, and who switched it last. */
const signoutCheckTitle = (t: TrainingRow): string => {
  const on = t.signout_check !== false;
  const what = on
    ? 'On: people not marked Registered in time cannot end their day.'
    : 'Off: this training does not stop anyone ending their day.';
  if (!t.signout_check_changed_at) return what;
  const by = t.signout_check_changed_by ? ` by ${t.signout_check_changed_by}` : '';
  return `${what} ${on ? 'Turned back on' : 'Turned off'} ${formatIST(t.signout_check_changed_at)}${by}.`;
};

/** Convert an ISO string (UTC) into the `YYYY-MM-DDTHH:mm` format the
 *  `<input type="datetime-local">` expects, computed in IST. */
const isoToLocalInput = (iso: string): string => {
  if (!iso) return '';
  // Use the IST-formatted parts to assemble the local-input string.
  const parts = new Date(iso).toLocaleString('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  // 'YYYY-MM-DD, HH:MM' -> 'YYYY-MM-DDTHH:MM'
  return parts.replace(', ', 'T').slice(0, 16);
};

/** Convert a `<input type="datetime-local">` value (interpreted as IST) into
 *  an ISO timestamp string that the backend can store as TIMESTAMPTZ. */
const localInputToIso = (value: string): string => {
  if (!value) return '';
  // Append IST offset; modern Date can parse this.
  return new Date(`${value}:00+05:30`).toISOString();
};

const PARAMETER_LABELS: Record<string, string> = {
  am_feedback: 'AM Feedback (avg rating)',
  seed_score: 'Seed Score',
  grooming: 'Grooming (Composite Avg)',
  random_attendance: 'Random Attendance Marked %',
  leave_policy: 'Leave Policy Adherence (Absent Days)',
  on_time: 'On Time Arrival (Late Count)',
  training_participation: 'Training Participation %',
};

const PARAMETER_ORDER = ['am_feedback', 'seed_score', 'grooming', 'random_attendance', 'leave_policy', 'on_time', 'training_participation'];

type SortColumn =
  | 'employee_code'
  | 'employee_name'
  | 'store_code'
  | 'store_name'
  | 'store_category'
  | 'store_trade'
  | 'am_value' | 'am_samples' | 'am_points'
  | 'seed_value' | 'seed_points'
  | 'groom_value'      | 'groom_points'
  | 'random_value'     | 'random_points'
  | 'leave_value'      | 'leave_points'
  | 'on_time_value'    | 'on_time_points'
  | 'training_value'   | 'training_scheduled' | 'training_attended' | 'training_points'
  | 'total';

type SortDirection = 'asc' | 'desc';

const compare = (a: any, b: any): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a ?? '').localeCompare(String(b ?? ''));
};

const sortValue = (row: EmployeeScorecard, col: SortColumn): any => {
  switch (col) {
    case 'employee_code':    return row.employee_code;
    case 'employee_name':    return row.employee_name;
    case 'store_code':       return row.store_code;
    case 'store_name':       return row.store_name;
    case 'store_category':   return row.store_category;
    case 'store_trade':      return row.store_trade;
    case 'am_value':         return row.am_feedback.value;
    case 'am_samples':       return row.am_feedback.sample_count;
    case 'am_points':        return row.am_feedback.points;
    case 'seed_value':       return row.seed_score.value;
    case 'seed_points':      return row.seed_score.points;
    case 'groom_value':      return row.grooming.value;
    case 'groom_points':     return row.grooming.points;
    case 'random_value':     return row.random.value;
    case 'random_points':    return row.random.points;
    case 'leave_value':      return row.leave_policy.value;
    case 'leave_points':     return row.leave_policy.points;
    case 'on_time_value':    return row.on_time.value;
    case 'on_time_points':   return row.on_time.points;
    case 'training_value':     return row.training.value;
    case 'training_scheduled': return row.training.scheduled;
    case 'training_attended':  return row.training.attended;
    case 'training_points':    return row.training.points;
    case 'total':            return row.total;
  }
};

const TEXT_SORT_COLS: SortColumn[] = [
  'employee_code', 'employee_name', 'store_code', 'store_name', 'store_category', 'store_trade',
];

// Dedupe + sort the values powering a filter dropdown (drops blanks).
const uniqSorted = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v && v.trim() !== ''))).sort((a, b) => a.localeCompare(b));

const blankTrainingForm = (): {
  name: string;
  training_datetime: string;       // datetime-local value
  training_type: 'in_person' | 'web_based';
  registration_url: string;
  duration_minutes: string;        // numeric input value
  employee_codes: Set<string>;
} => ({
  name: '',
  training_datetime: '',
  training_type: 'in_person',      // 'Classroom' by default
  registration_url: '',
  duration_minutes: '',
  employee_codes: new Set<string>(),
});

// Props supported when this page is rendered inline as a tab of the unified
// /reports/employee-score page (see EmployeeScorePage's Manage sub-tabs).
// Standalone usage (route render) leaves both props undefined — behaviour is
// unchanged from before.
interface AppleScorecardPageProps {
  /** When true: hides the outer page header (title + description) so this page
   *  blends into a parent shell that already has its own page chrome. */
  embedded?: boolean;
  /** When set: forces the page to a single tab and hides the inner tab nav.
   *  Used by the unified shell's Manage sub-tabs which each map 1:1 to a tab
   *  in this page (trainings, training_attendance, seed_scores). */
  restrictTab?: TabKey;
  /** When set: only these tabs render in the inner tab nav. Use this when
   *  embedding as a standalone module (e.g. the new /master-management/trainings
   *  page wants both 'trainings' and 'training_attendance' but not the rest). */
  showTabs?: TabKey[];
  /** The trainings list's name box, when the page embedding this one lets a link fill
   *  it (hooks/useLinkedSearch, called there: the address belongs to the page that is
   *  open, and Employee Score, which also embeds this, has a linked box of its own).
   *  Left out, the box is plain state. */
  trainingNameSearch?: LinkedSearch;
}

export default function AppleScorecardPage({ embedded, restrictTab, showTabs, trainingNameSearch: linkedTrainingName }: AppleScorecardPageProps = {}) {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const isAppleProject = selectedProjectId === APPLE_PROJECT_ID;
  // Trainings-only embed (the standalone Grow → Offline Trainings page).
  // Trainings are a generic per-project module — only the Behavioral Score
  // tabs are Apple-specific — so this mode skips the Apple project gate and
  // every trainings API call uses the SELECTED project (on the full Apple
  // page selectedProjectId === APPLE_PROJECT_ID, so behavior there is identical).
  const trainingsOnly = restrictTab === 'trainings';

  // When `restrictTab` is provided the page is locked to that tab — useful
  // when EmployeeScorePage embeds us as a single tab of its Manage view. The
  // internal `setTab` is still available for tabs that aren't pinned.
  // When `showTabs` is provided, the initial tab is the first entry in the
  // allow-list so we don't land on a hidden inner tab.
  const [tab, setTabRaw] = useState<TabKey>(
    restrictTab ?? (showTabs && showTabs.length > 0 ? showTabs[0] : 'scorecard'),
  );
  const setTab = (t: TabKey) => setTabRaw(restrictTab ?? t);
  const [periodInput, setPeriodInput] = useState(todayMonthInput());
  const period = useMemo(() => monthInputToPeriod(periodInput), [periodInput]);

  // ── Seed Scores review state ──────────────────────────────────────────────
  const [seedRows, setSeedRows] = useState<SeedScoreSubmissionRow[]>([]);
  const [loadingSeed, setLoadingSeed] = useState(false);
  const [seedQuestionId, setSeedQuestionId] = useState<string | null>(null);
  // Per-row edit buffer keyed by answer_id. Lets admin type freely without the
  // value snapping back to the server-extracted number until they hit Save.
  const [seedEditDraft, setSeedEditDraft] = useState<Record<string, string>>({});
  const [savingSeedAnswerId, setSavingSeedAnswerId] = useState<string | null>(null);

  // ── Scorecard state ────────────────────────────────────────────────────────
  const [scorecard, setScorecard] = useState<EmployeeScorecard[]>([]);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  const [downloadingScorecard, setDownloadingScorecard] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('total');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterText, setFilterText] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('');

  // ── Slabs state ───────────────────────────────────────────────────────────
  const [slabs, setSlabs] = useState<Record<string, SlabRow[]>>({});
  const [loadingSlabs, setLoadingSlabs] = useState(false);

  // ── Trainings state ───────────────────────────────────────────────────────
  const [trainings, setTrainings] = useState<TrainingRow[]>([]);
  // Trainings whose Sign-out check change is still saving (their switch is disabled meanwhile).
  const [signoutSavingIds, setSignoutSavingIds] = useState<Set<string>>(new Set());
  const [loadingTrainings, setLoadingTrainings] = useState(false);
  // Trainings list filters. The scheduled-date range replaces the old month
  // Period picker (defaults to the current calendar month). Name/URL search is
  // a pure client-side filter on the loaded list; the employee search is sent
  // to the backend (debounced) since assignees aren't in the list payload.
  const ownTrainingName = useState('');
  const [trainingNameSearch, setTrainingNameSearch] = linkedTrainingName ?? ownTrainingName;
  // Set while the name box still holds what a link put there (?q=<a training's name>).
  const trainingLink = linkedTrainingName?.[2] ?? null;
  // A link names one training, and it may be scheduled in any month, where this
  // month's list would say nothing matches. So for a link the list opens on every
  // date: both boxes empty, which the server reads as no date limit. Each later link
  // empties them again (below); after that the dates are the person's.
  const [trainingStart, setTrainingStart] = useState<string>(() => {
    if (trainingLink) return '';
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [trainingEnd, setTrainingEnd] = useState<string>(() => {
    if (trainingLink) return '';
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
  });
  useEffect(() => {
    if (!trainingLink) return;
    setTrainingStart('');
    setTrainingEnd('');
  }, [trainingLink]);
  const [trainingEmployeeSearch, setTrainingEmployeeSearch] = useState('');
  const [appliedEmployeeSearch, setAppliedEmployeeSearch] = useState('');
  // Client-side name / URL filter on top of the (date + employee) filtered list
  // returned by the backend.
  const visibleTrainings = useMemo(() => {
    const q = trainingNameSearch.trim().toLowerCase();
    if (!q) return trainings;
    return trainings.filter((t) =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.registration_url || '').toLowerCase().includes(q),
    );
  }, [trainings, trainingNameSearch]);
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [editingTrainingId, setEditingTrainingId] = useState<string | null>(null);
  const [trainingForm, setTrainingForm] = useState(blankTrainingForm());
  const [trainingFormSaving, setTrainingFormSaving] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState<EmployeeOption[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  // When true, the employee picker shows ONLY the currently-assigned
  // employees. Big projects can have thousands of employees in the list;
  // this lets the admin quickly review / remove who's already on the
  // training without scrolling through the full roster.
  const [employeeShowOnlySelected, setEmployeeShowOnlySelected] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  // Assignee-picker filters (values come from the loaded candidate list).
  const [filterBudget, setFilterBudget] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterAM, setFilterAM] = useState('');
  const [filterStoreCategory, setFilterStoreCategory] = useState('');
  const [filterStoreTrade, setFilterStoreTrade] = useState('');

  // ── Training Attendance state ─────────────────────────────────────────────
  const [expandedTrainingId, setExpandedTrainingId] = useState<string | null>(null);
  const [attendeesByTraining, setAttendeesByTraining] = useState<Record<string, AttendeeRow[]>>({});
  const [loadingAttendeesId, setLoadingAttendeesId] = useState<string | null>(null);
  const [uploadingAttendance, setUploadingAttendance] = useState(false);
  // Upload Attendance modal — replaces what used to be two separate top-row
  // buttons (Download Template + Upload Excel). The modal hosts the template
  // download inline so the workflow is contained.
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  // Staged file for the Upload Attendance modal — picked/dropped first, then
  // uploaded explicitly via the modal's Upload button (matches the Stores /
  // Targets bulk-upload flow).
  const [selectedAttendanceFile, setSelectedAttendanceFile] = useState<File | null>(null);
  const [attendanceUploadResult, setAttendanceUploadResult] = useState<
    { inserted?: number; updated: number; failed: number; errors: any[] } | null
  >(null);

  // Bulk Upload Trainings modal — mirrors the attendance upload flow.
  const [trainingsUploadModalOpen, setTrainingsUploadModalOpen] = useState(false);
  const [uploadingTrainings, setUploadingTrainings] = useState(false);
  const [downloadingTrainingsTemplate, setDownloadingTrainingsTemplate] = useState(false);
  const [downloadingTrainings, setDownloadingTrainings] = useState(false);
  const [selectedTrainingsFile, setSelectedTrainingsFile] = useState<File | null>(null);
  const [trainingsUploadResult, setTrainingsUploadResult] = useState<
    { created: number; skipped: number; attendeesAdded: number; failed: number; errors: any[] } | null
  >(null);

  // Upload Registrations modal — mirrors the Upload Attendance flow.
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [uploadingRegistration, setUploadingRegistration] = useState(false);
  const [downloadingRegistrationTemplate, setDownloadingRegistrationTemplate] = useState(false);
  const [selectedRegistrationFile, setSelectedRegistrationFile] = useState<File | null>(null);
  const [registrationUploadResult, setRegistrationUploadResult] = useState<
    { inserted?: number; updated: number; failed: number; errors: any[] } | null
  >(null);

  // Which bulk-upload's error list is currently being generated ('attendance' |
  // 'registration' | 'trainings'), so only that modal's button shows a spinner.
  const [downloadingUploadErrors, setDownloadingUploadErrors] = useState<string | null>(null);

  const [error, setError] = useState<string | null>(null);

  // ── Derived: store category options for filter dropdown ───────────────────
  const storeCategoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of scorecard) if (r.store_category) set.add(r.store_category);
    return Array.from(set).sort();
  }, [scorecard]);

  // ── Derived: filtered + sorted scorecard rows ─────────────────────────────
  const sortedScorecard = useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    const arr = scorecard.filter((r) => {
      if (filterCategory && r.store_category !== filterCategory) return false;
      if (!needle) return true;
      return (
        r.employee_code.toLowerCase().includes(needle) ||
        r.employee_name.toLowerCase().includes(needle) ||
        r.store_name.toLowerCase().includes(needle) ||
        r.store_code.toLowerCase().includes(needle) ||
        r.store_category.toLowerCase().includes(needle) ||
        r.store_trade.toLowerCase().includes(needle)
      );
    });
    arr.sort((a, b) => {
      const cmp = compare(sortValue(a, sortColumn), sortValue(b, sortColumn));
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [scorecard, sortColumn, sortDirection, filterText, filterCategory]);

  const handleSort = (col: SortColumn) => {
    if (col === sortColumn) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col);
      setSortDirection(TEXT_SORT_COLS.includes(col) ? 'asc' : 'desc');
    }
  };

  // ── Loaders ────────────────────────────────────────────────────────────────

  const loadScorecard = useCallback(async () => {
    if (!isAppleProject) return;
    setError(null);
    setLoadingScorecard(true);
    try {
      const res = await appleScorecardAPI.getScorecard(APPLE_PROJECT_ID, period);
      setScorecard(res.data?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load scorecard');
    } finally {
      setLoadingScorecard(false);
    }
  }, [isAppleProject, period]);

  const loadSeedSubmissions = useCallback(async () => {
    if (!isAppleProject) return;
    setLoadingSeed(true);
    setError(null);
    try {
      const res = await appleScorecardAPI.getSeedScoreSubmissions(APPLE_PROJECT_ID, period);
      const rows = (res.data?.data || []) as SeedScoreSubmissionRow[];
      setSeedRows(rows);
      setSeedQuestionId(res.data?.meta?.seed_question_id ?? null);
      // Seed the edit drafts from whatever value is currently on the row, so
      // the input starts pre-filled with the latest known number.
      const initialDrafts: Record<string, string> = {};
      for (const r of rows) {
        if (!r.answer_id) continue;
        const v = r.extracted_data?.value;
        if (v !== undefined && v !== null && v !== '') initialDrafts[r.answer_id] = String(v);
      }
      setSeedEditDraft(initialDrafts);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load seed score submissions');
    } finally {
      setLoadingSeed(false);
    }
  }, [isAppleProject, period]);

  const saveSeedOverride = async (answerId: string) => {
    const raw = seedEditDraft[answerId];
    const numeric = parseFloat(raw);
    if (!Number.isFinite(numeric)) {
      setError('Enter a valid number before saving.');
      return;
    }
    setSavingSeedAnswerId(answerId);
    setError(null);
    try {
      await tasksAPI.overrideAnswerExtractedData(answerId, numeric);
      // Refresh the list so the row reflects the saved value + the
      // employee_seed_scores aggregate picks up the new number on the next
      // Scorecard render.
      await loadSeedSubmissions();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save override');
    } finally {
      setSavingSeedAnswerId(null);
    }
  };

  const loadSlabs = useCallback(async () => {
    if (!isAppleProject) return;
    setLoadingSlabs(true);
    try {
      const res = await appleScorecardAPI.getSlabs(APPLE_PROJECT_ID);
      setSlabs(res.data?.data || {});
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load slabs');
    } finally {
      setLoadingSlabs(false);
    }
  }, [isAppleProject]);

  const loadTrainings = useCallback(async (overrides?: { start?: string; end?: string; employee?: string }) => {
    if (!selectedProjectId) return;
    setLoadingTrainings(true);
    try {
      const res = await appleScorecardAPI.listTrainings(selectedProjectId, {
        startDate: overrides?.start ?? trainingStart,
        endDate: overrides?.end ?? trainingEnd,
        employee: (overrides?.employee ?? appliedEmployeeSearch).trim() || undefined,
      });
      setTrainings(res.data?.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load trainings');
    } finally {
      setLoadingTrainings(false);
    }
  }, [selectedProjectId, trainingStart, trainingEnd, appliedEmployeeSearch]);

  const loadAttendees = useCallback(async (trainingId: string) => {
    if (!selectedProjectId) return;
    setLoadingAttendeesId(trainingId);
    try {
      const res = await appleScorecardAPI.listAttendees(selectedProjectId, trainingId);
      setAttendeesByTraining((m) => ({ ...m, [trainingId]: res.data?.data || [] }));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load attendees');
    } finally {
      setLoadingAttendeesId(null);
    }
  }, [selectedProjectId]);

  // Lazy-load the employee picker list (once the create/edit modal opens).
  // Enriched candidates carry city / area manager / store category / store trade
  // so the picker can filter by them.
  const loadEmployeeOptions = useCallback(async () => {
    if (!selectedProjectId) return;
    if (employeeOptions.length > 0) return;
    setLoadingEmployees(true);
    setEmployeesError(null);
    try {
      const res = await appleScorecardAPI.listTrainingCandidates(selectedProjectId);
      const data = (res.data?.data || []) as any[];
      setEmployeeOptions(
        data.map((e) => ({
          employee_code: String(e.employee_code || '').trim(),
          employee_name: e.employee_name || '',
          city: e.city || '',
          area_manager: e.area_manager || '',
          store_category: e.store_category || '',
          store_trade: e.store_trade || '',
          budget_id: String(e.budget_id ?? ''),
          budget_name: e.budget_name || '',
          designation: e.designation || '',
          is_store: e.is_store !== false,
        })).filter((e) => e.employee_code)
      );
    } catch (err: any) {
      setEmployeesError(err?.response?.data?.message || err?.message || 'Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  }, [selectedProjectId, employeeOptions.length]);

  // Project switch: drop project-scoped trainings caches so the previous
  // project's sessions / candidate list can't leak into the new one.
  useEffect(() => {
    setTrainings([]);
    setAttendeesByTraining({});
    setEmployeeOptions([]);
  }, [selectedProjectId]);

  // ── Tab effect: trigger the right loader on tab/period change ──────────────
  useEffect(() => {
    if (!isAppleProject && !trainingsOnly) return;
    if (tab === 'scorecard') loadScorecard();
    else if (tab === 'slabs') loadSlabs();
    else if (tab === 'trainings') loadTrainings();
    else if (tab === 'training_attendance') loadTrainings();
    else if (tab === 'seed_scores') loadSeedSubmissions();
  }, [tab, period, isAppleProject, trainingsOnly, loadScorecard, loadSlabs, loadTrainings]);

  // Debounce the trainings employee search before it hits the backend.
  useEffect(() => {
    const h = setTimeout(() => setAppliedEmployeeSearch(trainingEmployeeSearch.trim()), 350);
    return () => clearTimeout(h);
  }, [trainingEmployeeSearch]);

  // ── Scorecard download ─────────────────────────────────────────────────────
  const handleDownloadScorecard = async () => {
    setDownloadingScorecard(true);
    setError(null);
    try {
      const res = await appleScorecardAPI.downloadScorecard(APPLE_PROJECT_ID, period);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `apple_scorecard_${periodInput}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to download scorecard');
    } finally {
      setDownloadingScorecard(false);
    }
  };

  // ── Training CRUD ──────────────────────────────────────────────────────────

  const resetEmployeePicker = () => {
    setEmployeeSearch('');
    setFilterBudget('');
    setFilterCity('');
    setFilterAM('');
    setFilterStoreCategory('');
    setFilterStoreTrade('');
  };

  const openCreateTraining = () => {
    setEditingTrainingId(null);
    setTrainingForm(blankTrainingForm());
    resetEmployeePicker();
    setTrainingModalOpen(true);
    loadEmployeeOptions();
  };

  const openEditTraining = async (t: TrainingRow) => {
    setEditingTrainingId(t.id);
    resetEmployeePicker();
    setTrainingForm({
      name: t.name,
      training_datetime: isoToLocalInput(t.training_datetime),
      training_type: t.training_type === 'web_based' ? 'web_based' : 'in_person',
      registration_url: t.registration_url || '',
      duration_minutes: t.duration_minutes != null ? String(t.duration_minutes) : '',
      employee_codes: new Set<string>(),
    });
    setTrainingModalOpen(true);
    loadEmployeeOptions();
    // Pre-load current assignees
    try {
      if (!selectedProjectId) return;
      const res = await appleScorecardAPI.listAttendees(selectedProjectId, t.id);
      const assigned = (res.data?.data || []) as AttendeeRow[];
      setTrainingForm((f) => ({ ...f, employee_codes: new Set(assigned.map((a) => a.employee_code)) }));
    } catch (err: any) {
      console.warn('Failed to load existing assignees:', err?.message);
    }
  };

  const closeTrainingModal = () => {
    setTrainingModalOpen(false);
    setEditingTrainingId(null);
  };

  const handleSaveTraining = async () => {
    setError(null);
    if (!selectedProjectId) return;
    if (!trainingForm.name.trim()) { setError('Training name is required'); return; }
    if (!trainingForm.training_datetime) { setError('Training date/time is required'); return; }
    const url = trainingForm.registration_url.trim();
    if (url) {
      try { new URL(url); } catch { setError('Registration URL is not a valid URL'); return; }
    }

    setTrainingFormSaving(true);
    try {
      const payload = {
        name: trainingForm.name.trim(),
        training_datetime: localInputToIso(trainingForm.training_datetime),
        training_type: trainingForm.training_type,
        description: undefined,
        registration_url: url,
        duration_minutes: trainingForm.duration_minutes ? Number(trainingForm.duration_minutes) : null,
        employee_codes: Array.from(trainingForm.employee_codes),
      };
      if (editingTrainingId) {
        await appleScorecardAPI.updateTraining(selectedProjectId, editingTrainingId, payload);
      } else {
        await appleScorecardAPI.createTraining(selectedProjectId, payload);
      }
      closeTrainingModal();
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save training');
    } finally {
      setTrainingFormSaving(false);
    }
  };

  const handleCancelTraining = async (id: string) => {
    if (!selectedProjectId) return;
    if (!window.confirm('Cancel this training? Scoring will stop counting it.')) return;
    try {
      await appleScorecardAPI.deleteTraining(selectedProjectId, id);
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to cancel training');
    }
  };

  // Sign-out check On/Off. Off: this training no longer blocks End Day for anyone not yet
  // registered; nothing else changes. Back On: days it was Off do not count.
  const handleToggleSignoutCheck = async (t: TrainingRow) => {
    if (!selectedProjectId) return;
    const turnOn = t.signout_check === false;
    const question = turnOn
      ? `Turn the sign-out check back on for "${t.name}"?\n\nPeople not registered in time will not be able to end their day. Days it was off do not count.`
      : `Turn off the sign-out check for "${t.name}"?\n\nPeople who have not registered will be able to end their day. Nothing else about the training changes.`;
    if (!window.confirm(question)) return;
    setSignoutSavingIds((s) => new Set(s).add(t.id));
    try {
      const res = await appleScorecardAPI.setTrainingSignoutCheck(selectedProjectId, t.id, turnOn);
      const state = res.data?.data as Pick<TrainingRow, 'signout_check' | 'signout_check_changed_at' | 'signout_check_changed_by'> | undefined;
      setTrainings((rows) => rows.map((r) => (r.id === t.id ? { ...r, ...(state ?? { signout_check: turnOn }) } : r)));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to change the sign-out check');
    } finally {
      setSignoutSavingIds((s) => {
        const next = new Set(s);
        next.delete(t.id);
        return next;
      });
    }
  };

  const handleDeleteTraining = async (id: string) => {
    if (!selectedProjectId) return;
    if (!window.confirm('Permanently delete this cancelled training? This removes it and its assignees and cannot be undone.')) return;
    try {
      await appleScorecardAPI.deleteTraining(selectedProjectId, id, true);
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete training');
    }
  };

  // ── Attendance editing + bulk upload ───────────────────────────────────────

  const toggleExpandTraining = (id: string) => {
    if (expandedTrainingId === id) {
      setExpandedTrainingId(null);
    } else {
      setExpandedTrainingId(id);
      if (!attendeesByTraining[id]) loadAttendees(id);
    }
  };

  const handleToggleAttendance = async (trainingId: string, employee_code: string, attended: boolean) => {
    if (!selectedProjectId) return;
    // Optimistic update
    setAttendeesByTraining((m) => ({
      ...m,
      [trainingId]: (m[trainingId] || []).map((a) =>
        a.employee_code === employee_code ? { ...a, attended } : a
      ),
    }));
    try {
      await appleScorecardAPI.upsertAttendance(selectedProjectId, trainingId, { employee_code, attended });
      // Refresh counts on training row
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save attendance');
      // Revert
      setAttendeesByTraining((m) => ({
        ...m,
        [trainingId]: (m[trainingId] || []).map((a) =>
          a.employee_code === employee_code ? { ...a, attended: !attended } : a
        ),
      }));
    }
  };

  // Remove a candidate from a training. Only offered while attendance AND
  // registration are both unmarked; the backend re-enforces that and returns 409
  // if either was saved in the meantime (we then refresh so the row re-locks).
  const handleRemoveAttendee = async (trainingId: string, employee_code: string) => {
    if (!selectedProjectId) return;
    if (!window.confirm('Remove this candidate from the training?')) return;
    try {
      await appleScorecardAPI.removeAttendee(selectedProjectId, trainingId, employee_code);
      // Drop the row locally, then refresh the trainings list so the Assignees count updates.
      setAttendeesByTraining((m) => ({
        ...m,
        [trainingId]: (m[trainingId] || []).filter((a) => a.employee_code !== employee_code),
      }));
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to remove candidate');
      // A 409 means attendance/registration was saved meanwhile — reload so the
      // saved status shows and the Remove control disappears.
      loadAttendees(trainingId);
    }
  };

  const handleAttendanceFile = async (file: File) => {
    if (!selectedProjectId) return;
    setUploadingAttendance(true);
    setAttendanceUploadResult(null);
    setError(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await appleScorecardAPI.bulkUploadAttendance(selectedProjectId, b64);
      setAttendanceUploadResult(res.data?.data || null);
      setSelectedAttendanceFile(null);
      // Refresh trainings + any expanded list
      loadTrainings();
      if (expandedTrainingId) loadAttendees(expandedTrainingId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to upload attendance file');
    } finally {
      setUploadingAttendance(false);
    }
  };

  const handleDownloadAttendanceTemplate = async () => {
    if (!selectedProjectId) return;
    setDownloadingTemplate(true);
    try {
      const res = await appleScorecardAPI.downloadAttendanceTemplate(selectedProjectId);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'apple_training_attendance_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to download template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // Download the rows a bulk upload rejected as an xlsx the uploader can work
  // from — same Row / Training ID / Employee Code / reason the modal shows, plus
  // the Training NAME (resolved server-side) so a multi-training file is
  // traceable back to the source sheet. Nothing is persisted: the errors array
  // held in state is posted back, so this only works while the modal is open.
  const handleDownloadUploadErrors = async (
    kind: 'attendance' | 'registration' | 'trainings',
    errors: any[],
  ) => {
    if (!selectedProjectId || !errors || errors.length === 0) return;
    setDownloadingUploadErrors(kind);
    setError(null);
    try {
      const res = await appleScorecardAPI.exportBulkUploadErrors(selectedProjectId, kind, errors);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        kind === 'trainings' ? 'trainings_upload_errors.xlsx'
        : kind === 'registration' ? 'training_registration_upload_errors.xlsx'
        : 'training_attendance_upload_errors.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // responseType 'blob' means an error body arrives as a Blob, not JSON.
      setError('Failed to download the error list');
    } finally {
      setDownloadingUploadErrors(null);
    }
  };

  // Close the Upload Attendance modal and reset its transient state.
  const closeAttendanceModal = () => {
    setAttendanceModalOpen(false);
    setSelectedAttendanceFile(null);
    setAttendanceUploadResult(null);
  };

  // ── Bulk Upload Trainings ───────────────────────────────────────────────────
  const handleTrainingsFile = async (file: File) => {
    if (!selectedProjectId) return;
    setUploadingTrainings(true);
    setTrainingsUploadResult(null);
    setError(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await appleScorecardAPI.bulkUploadTrainings(selectedProjectId, b64);
      setTrainingsUploadResult(res.data?.data || null);
      setSelectedTrainingsFile(null);
      loadTrainings();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to upload trainings file');
    } finally {
      setUploadingTrainings(false);
    }
  };

  const handleDownloadTrainingsTemplate = async () => {
    if (!selectedProjectId) return;
    setDownloadingTrainingsTemplate(true);
    try {
      const res = await appleScorecardAPI.downloadTrainingsTemplate(selectedProjectId);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'apple_trainings_bulk_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to download template');
    } finally {
      setDownloadingTrainingsTemplate(false);
    }
  };

  // Download every visible training WITH its assignees (attendance + registration)
  // as one XLSX. Sends the same filters the list uses so the file matches the table.
  const handleDownloadTrainings = async () => {
    if (!selectedProjectId) return;
    setDownloadingTrainings(true);
    setError(null);
    try {
      const res = await appleScorecardAPI.exportTrainings(selectedProjectId, {
        startDate: trainingStart,
        endDate: trainingEnd,
        employee: appliedEmployeeSearch.trim() || undefined,
        name: trainingNameSearch.trim() || undefined,
      });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // An empty date box is no limit on that side, named as the server names it.
      link.download = `trainings_${trainingStart || 'all'}_to_${trainingEnd || 'all'}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to download trainings');
    } finally {
      setDownloadingTrainings(false);
    }
  };

  const closeTrainingsUploadModal = () => {
    setTrainingsUploadModalOpen(false);
    setSelectedTrainingsFile(null);
    setTrainingsUploadResult(null);
  };

  // ── Registration (admin-marked Yes/No) ─────────────────────────────────────
  const handleToggleRegistration = async (trainingId: string, employee_code: string, registered: boolean) => {
    if (!selectedProjectId) return;
    // Snapshot the prior value so we can revert precisely on error.
    const prev = (attendeesByTraining[trainingId] || []).find((a) => a.employee_code === employee_code)?.registered ?? null;
    // Optimistic update
    setAttendeesByTraining((m) => ({
      ...m,
      [trainingId]: (m[trainingId] || []).map((a) =>
        a.employee_code === employee_code ? { ...a, registered } : a
      ),
    }));
    try {
      await appleScorecardAPI.upsertRegistration(selectedProjectId, trainingId, { employee_code, registered });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save registration');
      // Revert
      setAttendeesByTraining((m) => ({
        ...m,
        [trainingId]: (m[trainingId] || []).map((a) =>
          a.employee_code === employee_code ? { ...a, registered: prev } : a
        ),
      }));
    }
  };

  const handleRegistrationFile = async (file: File) => {
    if (!selectedProjectId) return;
    setUploadingRegistration(true);
    setRegistrationUploadResult(null);
    setError(null);
    try {
      const b64 = await fileToBase64(file);
      const res = await appleScorecardAPI.bulkUploadRegistration(selectedProjectId, b64);
      setRegistrationUploadResult(res.data?.data || null);
      setSelectedRegistrationFile(null);
      // Refresh any expanded attendee list so the new values show.
      if (expandedTrainingId) loadAttendees(expandedTrainingId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to upload registration file');
    } finally {
      setUploadingRegistration(false);
    }
  };

  const handleDownloadRegistrationTemplate = async () => {
    if (!selectedProjectId) return;
    setDownloadingRegistrationTemplate(true);
    try {
      const res = await appleScorecardAPI.downloadRegistrationTemplate(selectedProjectId);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'apple_training_registration_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to download template');
    } finally {
      setDownloadingRegistrationTemplate(false);
    }
  };

  const closeRegistrationModal = () => {
    setRegistrationModalOpen(false);
    setSelectedRegistrationFile(null);
    setRegistrationUploadResult(null);
  };

  // ── Project gate (Behavioral Score only — trainings work for any project) ──
  if (!isAppleProject && !trainingsOnly) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <Award className="mx-auto mb-4 h-10 w-10 text-gray-400" />
        <h1 className="text-xl font-semibold text-gray-900">Behavioral Score</h1>
        <p className="mt-2 text-sm text-gray-600">
          The Behavioral Score is available only for the Apple Assisted Sales Program project.
          Switch to that project from the top-right project picker to view it.
        </p>
      </div>
    );
  }

  // Employees visible in the picker, filtered by the search box.
  // Dropdown options for the assignee-picker filters, derived from the candidates.
  const budgetOptions = useMemo(() => uniqSorted(employeeOptions.map((e) => e.budget_name)), [employeeOptions]);
  const cityOptions = useMemo(() => uniqSorted(employeeOptions.map((e) => e.city)), [employeeOptions]);
  const amOptions = useMemo(() => uniqSorted(employeeOptions.map((e) => e.area_manager)), [employeeOptions]);
  const categoryOptions = useMemo(() => uniqSorted(employeeOptions.map((e) => e.store_category)), [employeeOptions]);
  const tradeOptions = useMemo(() => uniqSorted(employeeOptions.map((e) => e.store_trade)), [employeeOptions]);

  const employeeOptionsFiltered = useMemo(() => {
    const needle = employeeSearch.trim().toLowerCase();
    const selectedSet = trainingForm.employee_codes;
    const filtered = employeeOptions.filter((e) => {
      if (employeeShowOnlySelected && !selectedSet.has(e.employee_code)) return false;
      if (needle && !(e.employee_code.toLowerCase().includes(needle) || (e.employee_name || '').toLowerCase().includes(needle))) return false;
      if (filterBudget && e.budget_name !== filterBudget) return false;
      if (filterCity && e.city !== filterCity) return false;
      if (filterAM && e.area_manager !== filterAM) return false;
      if (filterStoreCategory && e.store_category !== filterStoreCategory) return false;
      if (filterStoreTrade && e.store_trade !== filterStoreTrade) return false;
      return true;
    });
    // Pin already-selected employees to the top so the admin can immediately
    // see who's on the training (vs hunting through a 2000+ row list). Within
    // each group (selected vs unselected) we preserve the original employee
    // name order from the candidates API.
    return filtered.slice().sort((a, b) => {
      const aSel = selectedSet.has(a.employee_code) ? 0 : 1;
      const bSel = selectedSet.has(b.employee_code) ? 0 : 1;
      return aSel - bSel;
    });
  }, [employeeOptions, employeeSearch, filterBudget, filterCity, filterAM, filterStoreCategory, filterStoreTrade, employeeShowOnlySelected, trainingForm.employee_codes]);

  // "Select all" operates on the currently filtered set (the full match, even
  // beyond the 200 rows rendered), so admins can bulk-assign by city/AM/etc.
  const allFilteredSelected =
    employeeOptionsFiltered.length > 0 &&
    employeeOptionsFiltered.every((e) => trainingForm.employee_codes.has(e.employee_code));

  const toggleSelectAllFiltered = () => {
    setTrainingForm((f) => {
      const next = new Set(f.employee_codes);
      if (allFilteredSelected) {
        employeeOptionsFiltered.forEach((e) => next.delete(e.employee_code));
      } else {
        employeeOptionsFiltered.forEach((e) => next.add(e.employee_code));
      }
      return { ...f, employee_codes: next };
    });
  };

  return (
    <div className="space-y-6">
      {/* Outer page chrome — hidden when this page is rendered inside the
          unified Employee Score shell (it provides its own header). */}
      {!embedded && (
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Award className="h-7 w-7 text-primary-600" />
              <h1 className="text-2xl font-semibold text-gray-900">Behavioral Score</h1>
            </div>
            <p className="mt-1 text-sm text-gray-500">Monthly performance score (0–100) per employee for the Apple program.</p>
          </div>
          <PeriodMonthPicker value={periodInput} onChange={(e) => setPeriodInput(e.target.value)} />
        </div>
      )}

      {/* In embedded mode with no restrictTab, surface a slim right-aligned
          period strip. When a single tab is pinned (e.g. Training Sessions
          uses restrictTab='trainings'), the period control is folded INTO
          that tab's action row so the screen has no leading whitespace. */}
      {embedded && !restrictTab && (
        <div className="flex items-center justify-end">
          <PeriodMonthPicker value={periodInput} onChange={(e) => setPeriodInput(e.target.value)} />
        </div>
      )}

      {/* Inner tab nav — suppressed when the parent has restricted us to a
          single tab via restrictTab (each Manage sub-tab pins exactly one). */}
      {!restrictTab && (
        <div className="mb-4 border-b border-gray-200">
          <nav className="-mb-px flex gap-6">
            {([
              { key: 'scorecard' as const, label: 'Scorecard' },
              // Trainings is now a combined view (sessions + per-row inline
              // attendance). The legacy 'training_attendance' inner tab is
              // gone; its content is folded into 'trainings'.
              { key: 'trainings' as const, label: 'Trainings' },
              { key: 'seed_scores' as const, label: 'Seed Scores' },
              { key: 'slabs' as const, label: 'Slab Structure' },
            ]).filter((t) => !showTabs || showTabs.includes(t.key)).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                  tab === t.key ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {tab === 'scorecard' && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Filter by employee code / name or store code / name…"
                  className="w-80 rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
                {filterText && (
                  <button type="button" onClick={() => setFilterText('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    aria-label="Clear filter">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">All store categories</option>
                {storeCategoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {(filterText || filterCategory) && (
                <button type="button" onClick={() => { setFilterText(''); setFilterCategory(''); }}
                  className="text-xs text-primary-600 hover:underline">
                  Clear all
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {loadingScorecard && <p className="text-sm text-gray-500">Calculating…</p>}
              <button type="button" onClick={handleDownloadScorecard}
                disabled={loadingScorecard || downloadingScorecard || scorecard.length === 0}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
                {downloadingScorecard ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download
              </button>
              <button type="button" onClick={loadScorecard} disabled={loadingScorecard}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
                {loadingScorecard ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Recalculate
              </button>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <SortableTh col="employee_code" align="left" sticky sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Emp Code</SortableTh>
                  <SortableTh col="employee_name" align="left" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Emp Name</SortableTh>
                  <SortableTh col="store_code" align="left" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Store Code</SortableTh>
                  <SortableTh col="store_name" align="left" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Store Name</SortableTh>
                  <SortableTh col="store_category" align="left" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Category</SortableTh>
                  <SortableTh col="store_trade" align="left" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Trade</SortableTh>
                  <SortableTh col="am_value" align="right" title="Avg AM rating across all AM-submitted ratings for this iPro in the month" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>AM Avg</SortableTh>
                  <SortableTh col="am_samples" align="right" title="How many AM ratings averaged in" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>AM #</SortableTh>
                  <SortableTh col="am_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /20</SortableTh>
                  <SortableTh col="seed_value" align="right" title="Cohort-relative score against same-AON peers. <3 months tenure → flat 20." sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Seed</SortableTh>
                  <SortableTh col="seed_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /20</SortableTh>
                  <SortableTh col="groom_value" align="right" title="Composite avg /10" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Groom Avg</SortableTh>
                  <SortableTh col="groom_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /10</SortableTh>
                  <SortableTh col="random_value" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Random %</SortableTh>
                  <SortableTh col="random_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /10</SortableTh>
                  <SortableTh col="leave_value" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Absent Days</SortableTh>
                  <SortableTh col="leave_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /15</SortableTh>
                  <SortableTh col="on_time_value" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Late Count</SortableTh>
                  <SortableTh col="on_time_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /10</SortableTh>
                  <SortableTh col="training_value" align="right" title="Attended / Scheduled — full credit when nothing was scheduled" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Training %</SortableTh>
                  <SortableTh col="training_scheduled" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Scheduled</SortableTh>
                  <SortableTh col="training_attended" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Attended</SortableTh>
                  <SortableTh col="training_points" align="right" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Pts /15</SortableTh>
                  <SortableTh col="total" align="right" emphasize stickyRight sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Total /100</SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedScorecard.length === 0 && !loadingScorecard && (
                  <tr><td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={24}>No employees found for this period.</td></tr>
                )}
                {sortedScorecard.map((row) => (
                  <tr key={row.employee_code} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-3 py-2 font-mono text-xs text-gray-700">{row.employee_code}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{row.employee_name}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{row.store_code || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{row.store_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{row.store_category || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{row.store_trade || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.am_feedback.sample_count > 0 ? row.am_feedback.value.toFixed(2) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-500">{row.am_feedback.sample_count}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.am_feedback.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.seed_score.value || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.seed_score.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.grooming.value.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.grooming.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums" title={`${row.random.slots_marked} of ${row.random.slots_received}`}>{row.random.value.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.random.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.leave_policy.value}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.leave_policy.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.on_time.value}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.on_time.points}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.training.value.toFixed(1)}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.training.scheduled}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{row.training.attended}</td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.training.points}</td>
                    <td className="sticky right-0 z-10 bg-white px-3 py-2 text-right text-base font-semibold text-primary-700 tabular-nums shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'trainings' && (
        <div>
          {/* Combined view: training sessions list + inline attendance (each
              row expands to show its attendees). Earlier this was split into
              two tabs (Trainings + Training Attendance); merging them keeps
              everything in one table where each row's expansion reveals
              who's assigned and whether they attended. */}
          {/* Toolbar card: header row (title + result count + actions) above a
              grouped filter row (date range, name/URL search, employee search).
              When this page is pinned to a single tab (TrainingSessionsPage
              embed), the top-level period strip is suppressed so these filters
              live here. The date range defaults to the current calendar month. */}
          <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">Trainings</h3>
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                  {loadingTrainings ? 'Loading…' : `${visibleTrainings.length} shown`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setTrainingsUploadModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <Upload className="h-4 w-4" /> Bulk Upload Trainings
                </button>
                <button type="button" onClick={() => setAttendanceModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <Upload className="h-4 w-4" /> Upload Attendance
                </button>
                <button type="button" onClick={() => setRegistrationModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <Upload className="h-4 w-4" /> Upload Registrations
                </button>
                <button type="button" onClick={handleDownloadTrainings}
                  disabled={downloadingTrainings || loadingTrainings}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  {downloadingTrainings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  {downloadingTrainings ? 'Preparing…' : 'Download'}
                </button>
                <button type="button" onClick={openCreateTraining}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700">
                  <Plus className="h-4 w-4" /> Create Training
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-x-5 gap-y-3 border-t border-gray-100 bg-gray-50/60 px-4 py-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Scheduled date range</label>
                <div className="flex items-center gap-2">
                  <input
                    id="training-start"
                    type="date"
                    value={trainingStart}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTrainingStart(v);
                      // Keep the range valid without capping the picker: if the new
                      // start runs past the current end, carry the end forward so a
                      // future month is reachable in a single click.
                      if (v && trainingEnd && v > trainingEnd) setTrainingEnd(v);
                    }}
                    className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                  <span className="text-sm text-gray-400">–</span>
                  <input
                    id="training-end"
                    type="date"
                    value={trainingEnd}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTrainingEnd(v);
                      if (v && trainingStart && v < trainingStart) setTrainingStart(v);
                    }}
                    className="h-9 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500" htmlFor="training-name-search">Search name / URL</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="training-name-search"
                    type="text"
                    value={trainingNameSearch}
                    onChange={(e) => setTrainingNameSearch(e.target.value)}
                    placeholder="Training name or URL"
                    className="h-9 w-60 rounded-lg border border-gray-300 bg-white pl-9 pr-8 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                  {trainingNameSearch && (
                    <button type="button" onClick={() => setTrainingNameSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-semibold uppercase tracking-wide text-gray-500" htmlFor="training-emp-search">Employee code / name</label>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    id="training-emp-search"
                    type="text"
                    value={trainingEmployeeSearch}
                    onChange={(e) => setTrainingEmployeeSearch(e.target.value)}
                    placeholder="Assigned employee"
                    className="h-9 w-60 rounded-lg border border-gray-300 bg-white pl-9 pr-8 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                  {trainingEmployeeSearch && (
                    <button type="button" onClick={() => setTrainingEmployeeSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" aria-label="Clear">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {(trainingNameSearch || trainingEmployeeSearch) && (
                <button
                  type="button"
                  onClick={() => { setTrainingNameSearch(''); setTrainingEmployeeSearch(''); }}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Upload result lives inside the Upload Attendance modal now —
              keeping the table-top area uncluttered. */}

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700" style={{ width: '28px' }}></th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Training ID</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Invitation Sent (IST)</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Scheduled (IST)</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Registration URL</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Assignees</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Attended</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700"
                    title="On: people not marked Registered in time cannot end their day in the app. Off: this training does not stop anyone.">
                    Sign-out check
                  </th>
                  <th className="px-3 py-2 text-center font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visibleTrainings.length === 0 && !loadingTrainings && (
                  <tr><td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={11}>
                    {trainingNameSearch.trim() || appliedEmployeeSearch
                      ? 'No trainings match the current filters.'
                      : 'No trainings scheduled in this date range.'}
                  </td></tr>
                )}
                {visibleTrainings.map((t) => {
                  const expanded = expandedTrainingId === t.id;
                  const attendees = attendeesByTraining[t.id] || [];
                  const loadingThis = loadingAttendeesId === t.id;
                  return (
                  <React.Fragment key={t.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => toggleExpandTraining(t.id)}
                        className="rounded p-0.5 hover:bg-gray-200" aria-label={expanded ? 'Collapse' : 'Expand'}>
                        {expanded ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{t.training_code || '—'}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        {t.name}
                        {t.onboarding_batch_id != null && (
                          <span
                            className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-700"
                            title="Created from an onboarding batch — part of a new joiner's onboarding journey, not a normal training"
                          >
                            Onboarding
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{t.created_at ? formatIST(t.created_at) : '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{formatIST(t.training_datetime)}</td>
                    <td className="px-3 py-2">
                      {t.registration_url
                        ? <a href={t.registration_url} target="_blank" rel="noopener noreferrer"
                            title={t.registration_url}
                            className="block max-w-[220px] truncate text-primary-600 hover:underline">{t.registration_url}</a>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.scheduled_count}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.attended_count}</td>
                    <td className="px-3 py-2">
                      {t.status === 'cancelled'
                        ? <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">Cancelled</span>
                        /* Expired only once the full window (start + duration) has
                           passed, matching the mobile app's invite-card expiry rule. */
                        : new Date(t.training_datetime).getTime() + (t.duration_minutes ?? 0) * 60_000 < Date.now()
                          ? <span className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">Expired</span>
                          : <span className="inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">Active</span>}
                    </td>
                    <td className="px-3 py-2">
                      {signoutCheckApplies(t)
                        ? <span title={signoutCheckTitle(t)}><ActiveStatusPill active={t.signout_check !== false} onText="On" offText="Off" /></span>
                        : <span className="text-gray-400" title="Only an upcoming training can stop someone ending their day.">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <TableActionGroup>
                        <TableActionButton action="edit" label="Edit" type="button" onClick={() => openEditTraining(t)} />
                        {/* The lock: closed while the check is On (End Day blocked), so
                            the button offers to open it; open while Off. The lock glyphs,
                            not activate/deactivate, because deactivate shares Cancel's icon. */}
                        {signoutCheckApplies(t) && (
                          <TableActionButton
                            action={t.signout_check !== false ? 'unlock' : 'lock'}
                            type="button"
                            role="switch"
                            aria-checked={t.signout_check !== false}
                            label={`${t.signout_check !== false ? 'Turn off' : 'Turn on'} the sign-out check for ${t.name}`}
                            title={t.signout_check !== false ? 'Turn off the sign-out check' : 'Turn on the sign-out check'}
                            onClick={() => void handleToggleSignoutCheck(t)}
                            disabled={signoutSavingIds.has(t.id)}
                          />
                        )}
                        {t.status === 'active' ? (
                          <TableActionButton action="cancel" label="Cancel" type="button" onClick={() => handleCancelTraining(t.id)} />
                        ) : (
                          <TableActionButton action="delete" label="Delete permanently" type="button" onClick={() => handleDeleteTraining(t.id)} title="Delete permanently" />
                        )}
                      </TableActionGroup>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={11} className="px-0 py-0">
                        <div className="border-t border-gray-200 px-4 py-3">
                          {loadingThis ? (
                            <InlineLoader label="Loading attendees…" className="flex" />
                          ) : attendees.length === 0 ? (
                            <p className="text-sm text-gray-500">No employees assigned to this training.</p>
                          ) : (
                            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Employee Code</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Employee Name</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Attended</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Registered</th>
                                    <th className="px-3 py-2 text-left font-medium text-gray-700">Updated Date</th>
                                    <th className="px-3 py-2 text-center font-medium text-gray-700">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {attendees.map((a) => {
                                    // "Updated Date" = whichever status was marked most recently.
                                    const updatedAt = [a.attendance_marked_at, a.registered_at]
                                      .filter(Boolean)
                                      .sort()
                                      .pop();
                                    return (
                                    <tr key={a.id} className="hover:bg-gray-50">
                                      <td className="px-3 py-2 font-mono text-xs text-gray-700">{a.employee_code}</td>
                                      <td className="px-3 py-2 text-gray-700">{a.employee_name || '—'}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-4">
                                          <label className="inline-flex cursor-pointer items-center gap-1.5">
                                            <input type="radio" name={`attended-${a.id}`} checked={a.attended === true}
                                              className="cursor-pointer accent-green-600"
                                              onChange={() => handleToggleAttendance(t.id, a.employee_code, true)} />
                                            <span className={a.attended === true ? 'font-medium text-green-700' : 'text-gray-600'}>Yes</span>
                                          </label>
                                          <label className="inline-flex cursor-pointer items-center gap-1.5">
                                            <input type="radio" name={`attended-${a.id}`} checked={a.attended === false}
                                              className="cursor-pointer accent-gray-500"
                                              onChange={() => handleToggleAttendance(t.id, a.employee_code, false)} />
                                            <span className={a.attended === false ? 'font-medium text-gray-700' : 'text-gray-600'}>No</span>
                                          </label>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-4">
                                          <label className="inline-flex cursor-pointer items-center gap-1.5">
                                            <input type="radio" name={`registered-${a.id}`} checked={a.registered === true}
                                              className="cursor-pointer accent-green-600"
                                              onChange={() => handleToggleRegistration(t.id, a.employee_code, true)} />
                                            <span className={a.registered === true ? 'font-medium text-green-700' : 'text-gray-600'}>Yes</span>
                                          </label>
                                          <label className="inline-flex cursor-pointer items-center gap-1.5">
                                            <input type="radio" name={`registered-${a.id}`} checked={a.registered === false}
                                              className="cursor-pointer accent-gray-500"
                                              onChange={() => handleToggleRegistration(t.id, a.employee_code, false)} />
                                            <span className={a.registered === false ? 'font-medium text-gray-700' : 'text-gray-600'}>No</span>
                                          </label>
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-xs text-gray-500">
                                        {updatedAt ? formatIST(updatedAt) : '—'}
                                      </td>
                                      <td className="text-center px-3 py-2"><TableActionGroup>
                                        {/* Removable only while BOTH attendance and registration are unmarked.
                                            Once either is saved the candidate is locked (no button). */}
                                        {a.attended == null && a.registered == null ? (
                                          <TableActionButton action="remove" label="Remove candidate" type="button" onClick={() => handleRemoveAttendee(t.id, a.employee_code)} title="Remove candidate from training" />
                                        ) : (
                                          <span className="text-gray-300">—</span>
                                        )}
                                      </TableActionGroup></td>
                                    </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'seed_scores' && (
        <div>
          <div className="mb-3 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
            Review the Seed Score uploaded by each employee for this month. Our OCR auto-extracts a number from the
            uploaded image; if it misreads, edit the value below and click <strong>Save</strong>. Overrides update the
            Apple Scorecard immediately. Until the next mobile build ships, employees cannot edit the value themselves —
            this surface is the source of truth.
          </div>

          {loadingSeed ? (
            <InlineLoader label="Loading seed score submissions…" className="flex" />
          ) : error ? (
            // Error is already surfaced by the top-of-page banner; suppress
            // the empty-state hint below so the admin isn't given misleading
            // "configure it like this" guidance for a request that actually failed.
            null
          ) : !seedQuestionId ? (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              No question in this project is marked as the <strong>Seed Score source</strong> yet. Open the Tasks page,
              edit a photo question for the Seed Score task, and toggle "Extract data from photo" + "Use as Seed Score
              source (Apple)" on. Submissions will appear here after that.
            </div>
          ) : seedRows.length === 0 ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">No active employees in this project.</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium text-gray-600">
                    <th className="px-3 py-2">Photo</th>
                    <th className="px-3 py-2">Employee Name</th>
                    <th className="px-3 py-2">Employee Code</th>
                    <th className="px-3 py-2">Store Name</th>
                    <th className="px-3 py-2">Store Code</th>
                    <th className="px-3 py-2">Store Address</th>
                    <th className="px-3 py-2">Submitted</th>
                    <th className="px-3 py-2">Extracted value</th>
                    <th className="text-center px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {seedRows.map((row) => {
                    const ans = row.answer_id;
                    const draftKey = ans || '';
                    const draftValue = ans ? (seedEditDraft[draftKey] ?? '') : '';
                    const overridden = !!row.extracted_data?.admin_overridden_at;
                    // photo_url is the raw Azure blob path from task_answers — serve it through
                    // the /api/images/view proxy (same fix as the Store Visits drill-down), else
                    // the <img>/<a> point at a relative path the SPA swallows.
                    const photoHref = getImageViewUrl(row.photo_url);
                    return (
                      <tr key={row.employee_code} className="border-b border-gray-100 last:border-0">
                        <td className="px-3 py-2">
                          {photoHref ? (
                            <a href={photoHref} target="_blank" rel="noreferrer">
                              <img
                                src={photoHref}
                                alt="Seed score upload"
                                className="h-12 w-12 rounded border border-gray-200 object-cover"
                                loading="lazy"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">No upload</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {row.employee_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600 tabular-nums">{row.employee_code}</td>
                        <td className="px-3 py-2 text-gray-700">{row.store_name || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 tabular-nums">{row.store_code || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 max-w-[240px]" title={row.store_address || ''}>
                          {row.store_address || '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {row.submitted_at ? new Date(row.submitted_at).toLocaleString() : <span className="text-gray-400">Not submitted</span>}
                        </td>
                        <td className="px-3 py-2">
                          {ans ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                step="0.01"
                                value={draftValue}
                                onChange={(e) => setSeedEditDraft((m) => ({ ...m, [draftKey]: e.target.value }))}
                                className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="—"
                              />
                              {overridden && (
                                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" title={`Overridden ${new Date(row.extracted_data!.admin_overridden_at!).toLocaleString()}`}>
                                  Edited
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center"><TableActionGroup>
                          {ans && (
                            <TableActionButton action="save" label="Save override" loading={savingSeedAnswerId === ans} type="button" disabled={savingSeedAnswerId === ans || !draftValue} onClick={() => saveSeedOverride(ans)} />
                          )}
                        </TableActionGroup></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'slabs' && (
        <div>
          {loadingSlabs ? (
            <InlineLoader label="Loading slabs…" className="flex" />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {PARAMETER_ORDER.map((param) => {
                const rows = slabs[param] || [];
                // Seed Score doesn't use the slab table — it's scored against
                // an AON (Age on Network) peer cohort, with a honeymoon rule
                // for new joiners. Render the actual scoring rules inline so
                // admins can see exactly how the column is computed.
                if (param === 'seed_score') {
                  return (
                    <div key={param} className="rounded-lg border border-gray-200 bg-white p-4">
                      <h3 className="mb-2 font-semibold text-gray-900">{PARAMETER_LABELS[param] || param}</h3>
                      <p className="mb-3 text-xs text-gray-500">
                        Cohort-relative scoring against same-project employees in the same Age-on-Network band.
                        Not driven by the <code className="rounded bg-gray-100 px-1">apple_scorecard_slabs</code> table.
                      </p>
                      <div className="mb-3 rounded border border-primary-200 bg-primary-50/40 p-2 text-xs text-primary-900">
                        <span className="font-medium">Honeymoon rule:</span> employees with date_of_joining within the last 3 months automatically receive the full <strong>20 points</strong>, no cohort comparison.
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="py-1 text-left font-medium text-gray-600">Position vs cohort</th>
                            <th className="py-1 text-right font-medium text-gray-600">Points</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Score &gt; median + σ</td><td className="py-1 text-right font-medium tabular-nums">20</td></tr>
                          <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">median ≤ score ≤ median + σ</td><td className="py-1 text-right font-medium tabular-nums">15</td></tr>
                          <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">median − σ ≤ score &lt; median</td><td className="py-1 text-right font-medium tabular-nums">10</td></tr>
                          <tr className="border-b border-gray-100"><td className="py-1 text-gray-700">Score &lt; median − σ</td><td className="py-1 text-right font-medium tabular-nums">0</td></tr>
                          <tr className="last:border-0"><td className="py-1 text-gray-700">No submission this month</td><td className="py-1 text-right font-medium tabular-nums">0</td></tr>
                        </tbody>
                      </table>
                      <div className="mt-3 text-[11px] text-gray-500">
                        <div className="font-medium text-gray-600">AON cohort buckets</div>
                        <div>3–6m · 6–9m · 9–12m · 1–2y · 2y+ (half-open intervals from <code>date_of_joining</code>)</div>
                        <div className="mt-1">Cohort size ≤ 1 in a bucket → flat 20 points (no meaningful peer comparison).</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={param} className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="mb-2 font-semibold text-gray-900">{PARAMETER_LABELS[param] || param}</h3>
                    {rows.length === 0 ? (
                      <p className="rounded border border-dashed border-gray-300 p-3 text-xs text-gray-500">
                        Pending configuration — calculation logic TBD. All employees currently score 0 for this parameter.
                      </p>
                    ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="py-1 text-left font-medium text-gray-600">Slab</th>
                          <th className="py-1 text-right font-medium text-gray-600">Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((s) => (
                          <tr key={s.display_order} className="border-b border-gray-100 last:border-0">
                            <td className="py-1 text-gray-700">{s.display_label}</td>
                            <td className="py-1 text-right font-medium tabular-nums">{s.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-4 text-xs text-gray-500">
            Slabs are stored in <code className="rounded bg-gray-100 px-1">apple_scorecard_slabs</code> and applied at scorecard calculation time. Editing is currently DB-only.
          </p>
        </div>
      )}

      {/* Upload Attendance modal — replaces the separate Attendance Template
          button. The modal hosts the template download (step 1) + the file
          picker (step 2) so the whole bulk-upload flow is contained. The
          upload result banner is also rendered here so the admin sees the
          outcome without losing the modal context. */}
      <Modal
        isOpen={attendanceModalOpen}
        onClose={closeAttendanceModal}
        title="Upload Training Attendance"
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeAttendanceModal}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => selectedAttendanceFile && handleAttendanceFile(selectedAttendanceFile)}
              disabled={!selectedAttendanceFile || uploadingAttendance}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploadingAttendance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploadingAttendance ? 'Uploading…' : 'Upload'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Step 1 — download the pre-filled template */}
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Step 1 — Download the template</p>
              <p className="mt-0.5 text-xs text-blue-700">
                Pre-filled with this period's trainings and assigned employees. Fill in the <em>Attended</em> column
                (Yes / No) and save before uploading.
              </p>
              <button
                type="button"
                onClick={handleDownloadAttendanceTemplate}
                disabled={downloadingTemplate}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                {downloadingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download Template
              </button>
            </div>
          </div>

          {/* Step 2 — drag & drop / pick the filled file */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-900">Step 2 — Upload the filled file</p>
            <label
              htmlFor="attendance-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) setSelectedAttendanceFile(f);
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/40"
            >
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-700">
                <span className="font-medium text-primary-600">Click to browse</span> or drag &amp; drop
              </p>
              <p className="mt-0.5 text-xs text-gray-500">Accepts .xlsx, .xls or .csv</p>
              <input
                id="attendance-file-input"
                type="file"
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setSelectedAttendanceFile(f);
                  e.target.value = '';
                }}
              />
            </label>

            {selectedAttendanceFile && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-sm text-gray-700">{selectedAttendanceFile.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    ({(selectedAttendanceFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedAttendanceFile(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Result — stat grid + error table */}
          {attendanceUploadResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" /> Upload complete
              </div>
              <div className="grid grid-cols-3 gap-3">
                {typeof attendanceUploadResult.inserted === 'number' && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                    <div className="text-2xl font-semibold text-green-700">{attendanceUploadResult.inserted}</div>
                    <div className="text-xs text-green-600">Inserted</div>
                  </div>
                )}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-blue-700">{attendanceUploadResult.updated}</div>
                  <div className="text-xs text-blue-600">Updated</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-red-700">{attendanceUploadResult.failed}</div>
                  <div className="text-xs text-red-600">Failed</div>
                </div>
              </div>

              {attendanceUploadResult.errors && attendanceUploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  {/* The rejected rows are only in memory (nothing is persisted),
                      so the download has to happen before this modal is closed. */}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium text-red-700">{attendanceUploadResult.errors.length}</span> row
                      {attendanceUploadResult.errors.length === 1 ? '' : 's'} not uploaded — download the list, fix them and re-upload.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleDownloadUploadErrors('attendance', attendanceUploadResult.errors)}
                      disabled={downloadingUploadErrors === 'attendance'}
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {downloadingUploadErrors === 'attendance' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Download error list
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-red-200">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="min-w-full divide-y divide-red-100 text-sm">
                        <thead className="sticky top-0 bg-red-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Row</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Training ID</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Employee Code</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-50">
                          {attendanceUploadResult.errors.map((e: any, i: number) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700">{e.row ?? '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.training_id || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.employee_code || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Upload Registrations modal — admin-marked Registered (Yes/No) in bulk.
          Mirrors the Upload Attendance flow: template download (step 1) + file
          picker (step 2) + result banner. Updates existing assignees only. */}
      <Modal
        isOpen={registrationModalOpen}
        onClose={closeRegistrationModal}
        title="Upload Training Registrations"
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeRegistrationModal}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => selectedRegistrationFile && handleRegistrationFile(selectedRegistrationFile)}
              disabled={!selectedRegistrationFile || uploadingRegistration}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploadingRegistration ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploadingRegistration ? 'Uploading…' : 'Upload'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Step 1 — download the pre-filled template */}
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Step 1 — Download the template</p>
              <p className="mt-0.5 text-xs text-blue-700">
                Pre-filled with this period's trainings and assigned employees. Fill in the <em>Registered</em> column
                (Yes / No) and save before uploading.
              </p>
              <button
                type="button"
                onClick={handleDownloadRegistrationTemplate}
                disabled={downloadingRegistrationTemplate}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                {downloadingRegistrationTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download Template
              </button>
            </div>
          </div>

          {/* Step 2 — drag & drop / pick the filled file */}
          <div>
            <p className="mb-2 text-sm font-medium text-gray-900">Step 2 — Upload the filled file</p>
            <label
              htmlFor="registration-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) setSelectedRegistrationFile(f);
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/40"
            >
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-700">
                <span className="font-medium text-primary-600">Click to browse</span> or drag &amp; drop
              </p>
              <p className="mt-0.5 text-xs text-gray-500">Accepts .xlsx, .xls or .csv</p>
              <input
                id="registration-file-input"
                type="file"
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setSelectedRegistrationFile(f);
                  e.target.value = '';
                }}
              />
            </label>

            {selectedRegistrationFile && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-sm text-gray-700">{selectedRegistrationFile.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    ({(selectedRegistrationFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedRegistrationFile(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Result — stat grid + error table */}
          {registrationUploadResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" /> Upload complete
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-blue-700">{registrationUploadResult.updated}</div>
                  <div className="text-xs text-blue-600">Updated</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-red-700">{registrationUploadResult.failed}</div>
                  <div className="text-xs text-red-600">Failed</div>
                </div>
              </div>

              {registrationUploadResult.errors && registrationUploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  {/* The rejected rows are only in memory (nothing is persisted),
                      so the download has to happen before this modal is closed. */}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium text-red-700">{registrationUploadResult.errors.length}</span> row
                      {registrationUploadResult.errors.length === 1 ? '' : 's'} not uploaded — download the list, fix them and re-upload.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleDownloadUploadErrors('registration', registrationUploadResult.errors)}
                      disabled={downloadingUploadErrors === 'registration'}
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {downloadingUploadErrors === 'registration' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Download error list
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-red-200">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="min-w-full divide-y divide-red-100 text-sm">
                        <thead className="sticky top-0 bg-red-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Row</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Training ID</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Employee Code</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-50">
                          {registrationUploadResult.errors.map((e: any, i: number) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700">{e.row ?? '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.training_id || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.employee_code || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Bulk Upload Trainings modal — create many trainings at once from a
          file (Name / Date-Time / Type / URL / Duration), one row per
          training+employee. Mirrors the attendance upload flow. */}
      <Modal
        isOpen={trainingsUploadModalOpen}
        onClose={closeTrainingsUploadModal}
        title="Bulk Upload Trainings"
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeTrainingsUploadModal}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => selectedTrainingsFile && handleTrainingsFile(selectedTrainingsFile)}
              disabled={!selectedTrainingsFile || uploadingTrainings}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {uploadingTrainings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploadingTrainings ? 'Uploading…' : 'Upload'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">Step 1 — Download the template</p>
              <p className="mt-0.5 text-xs text-blue-700">
                Columns: Training Name, Date / Time (IST), Training Type (<em>Classroom</em> or <em>Webex</em>),
                Registration URL, Duration (minutes), Employee Code. Put <strong>one employee per row</strong> and
                repeat the training details for each attendee — rows sharing the same Name + Date/Time become one training.
              </p>
              <button
                type="button"
                onClick={handleDownloadTrainingsTemplate}
                disabled={downloadingTrainingsTemplate}
                className="mt-2 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                {downloadingTrainingsTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download Template
              </button>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-900">Step 2 — Upload the filled file</p>
            <label
              htmlFor="trainings-file-input"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) setSelectedTrainingsFile(f);
              }}
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8 text-center transition-colors hover:border-primary-400 hover:bg-primary-50/40"
            >
              <Upload className="h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-700">
                <span className="font-medium text-primary-600">Click to browse</span> or drag &amp; drop
              </p>
              <p className="mt-0.5 text-xs text-gray-500">Accepts .xlsx, .xls or .csv</p>
              <input
                id="trainings-file-input"
                type="file"
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setSelectedTrainingsFile(f);
                  e.target.value = '';
                }}
              />
            </label>

            {selectedTrainingsFile && (
              <div className="mt-2 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center gap-2 truncate">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-sm text-gray-700">{selectedTrainingsFile.name}</span>
                  <span className="shrink-0 text-xs text-gray-400">
                    ({(selectedTrainingsFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedTrainingsFile(null)}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Remove file"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {trainingsUploadResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle className="h-4 w-4" /> Upload complete
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-green-700">{trainingsUploadResult.created}</div>
                  <div className="text-xs text-green-600">Created</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-blue-700">{trainingsUploadResult.attendeesAdded}</div>
                  <div className="text-xs text-blue-600">Assignees</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-amber-700">{trainingsUploadResult.skipped}</div>
                  <div className="text-xs text-amber-600">Skipped</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <div className="text-2xl font-semibold text-red-700">{trainingsUploadResult.failed}</div>
                  <div className="text-xs text-red-600">Failed</div>
                </div>
              </div>

              {trainingsUploadResult.errors && trainingsUploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium text-red-700">{trainingsUploadResult.errors.length}</span> row
                      {trainingsUploadResult.errors.length === 1 ? '' : 's'} not uploaded — download the list, fix them and re-upload.
                    </p>
                    <button
                      type="button"
                      onClick={() => handleDownloadUploadErrors('trainings', trainingsUploadResult.errors)}
                      disabled={downloadingUploadErrors === 'trainings'}
                      className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {downloadingUploadErrors === 'trainings' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                      Download error list
                    </button>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-red-200">
                    <div className="max-h-48 overflow-y-auto">
                      <table className="min-w-full divide-y divide-red-100 text-sm">
                        <thead className="sticky top-0 bg-red-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Row</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Training</th>
                            <th className="px-3 py-2 text-left font-medium text-red-700">Note</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-50">
                          {trainingsUploadResult.errors.map((e: any, i: number) => (
                            <tr key={i}>
                              <td className="px-3 py-1.5 text-gray-700">{e.row ?? '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.training || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-700">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {trainingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingTrainingId ? 'Edit Training' : 'Create Training'}
              </h2>
              <button type="button" onClick={closeTrainingModal} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Training Name *</label>
                <input type="text" value={trainingForm.name}
                  onChange={(e) => setTrainingForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  placeholder="e.g., Apple iPhone 17 launch session" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Training Type *</label>
                  <select value={trainingForm.training_type}
                    onChange={(e) => setTrainingForm((f) => ({ ...f, training_type: e.target.value as 'in_person' | 'web_based' }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="in_person">Classroom</option>
                    <option value="web_based">Webex</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Date / Time (IST) *</label>
                  <input type="datetime-local" value={trainingForm.training_datetime}
                    onChange={(e) => setTrainingForm((f) => ({ ...f, training_datetime: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Registration URL</label>
                  <input type="url" value={trainingForm.registration_url}
                    onChange={(e) => setTrainingForm((f) => ({ ...f, registration_url: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    placeholder="https://… (tap to register)" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Duration (minutes)</label>
                  <input type="number" min="0" value={trainingForm.duration_minutes}
                    onChange={(e) => setTrainingForm((f) => ({ ...f, duration_minutes: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    placeholder="e.g., 60" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Assigned Employees ({trainingForm.employee_codes.size})
                </label>
                <div className="mt-1 grid grid-cols-2 gap-2 md:grid-cols-3">
                  <select value={filterBudget} onChange={(e) => setFilterBudget(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="">All Budgets</option>
                    {budgetOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <select value={filterCity} onChange={(e) => setFilterCity(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="">All Cities</option>
                    {cityOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={filterAM} onChange={(e) => setFilterAM(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="">All Assistant Managers</option>
                    {amOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select value={filterStoreCategory} onChange={(e) => setFilterStoreCategory(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="">All Store Categories</option>
                    {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={filterStoreTrade} onChange={(e) => setFilterStoreTrade(e.target.value)}
                    className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100">
                    <option value="">All Store Trades</option>
                    {tradeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <input type="text" value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  placeholder="Search by code or name…"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100" />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 px-1">
                  <div className="flex items-center gap-3">
                    {employeeOptionsFiltered.length > 0 && (
                      <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-700">
                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered} />
                        {allFilteredSelected ? 'Deselect all' : 'Select all'} ({employeeOptionsFiltered.length})
                      </label>
                    )}
                    {/* "Show only selected" filter — surfaces who's already on
                        the training without scrolling through every employee
                        in the project. Especially useful when editing an
                        existing training to drop someone. */}
                    <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={employeeShowOnlySelected}
                        onChange={(e) => setEmployeeShowOnlySelected(e.target.checked)}
                      />
                      Show only selected
                    </label>
                  </div>
                  <span className="text-xs text-gray-400">{trainingForm.employee_codes.size} selected</span>
                </div>
                <div className="mt-2 h-56 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                  {employeeOptionsFiltered.length === 0 ? (
                    employeesError ? (
                      <p className="p-3 text-xs text-red-600">{employeesError}</p>
                    ) : loadingEmployees ? (
                      <p className="p-3 text-xs text-gray-500"><InlineLoader label="Loading employees…" size="xs" className="flex" /></p>
                    ) : (
                      <p className="p-3 text-xs text-gray-500">No matches.</p>
                    )
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {employeeOptionsFiltered.slice(0, 200).map((e) => {
                        const checked = trainingForm.employee_codes.has(e.employee_code);
                        return (
                          <li key={e.employee_code} className={checked ? 'bg-primary-50/60' : 'bg-white'}>
                            <label className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm ${checked ? 'hover:bg-primary-100/50' : 'hover:bg-gray-50'}`}>
                              <input type="checkbox" checked={checked}
                                onChange={() => {
                                  setTrainingForm((f) => {
                                    const next = new Set(f.employee_codes);
                                    if (checked) next.delete(e.employee_code);
                                    else next.add(e.employee_code);
                                    return { ...f, employee_codes: next };
                                  });
                                }} />
                              <span className="font-mono text-xs text-gray-600">{e.employee_code}</span>
                              {e.employee_name && e.employee_name !== e.employee_code && (
                                <span className="text-gray-800">{e.employee_name}</span>
                              )}
                              {/* Non-store staff (Area Managers, Trade Heads, …) carry no
                                  store attributes — badge their designation so admins can
                                  tell them apart from store employees at a glance. */}
                              {!e.is_store && (
                                <span className="ml-auto shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                                  {e.designation || 'No store'}
                                </span>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {employeeOptionsFiltered.length > 200 && (
                  <p className="mt-1 text-xs text-gray-500">Showing first 200 — refine your search to see more.</p>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-6 py-3">
              <button type="button" onClick={closeTrainingModal}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={handleSaveTraining} disabled={trainingFormSaving}
                className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {trainingFormSaving && <Loader2 className="h-3 w-3 animate-spin" />}
                <Save className="h-3.5 w-3.5" />
                {editingTrainingId ? 'Save Changes' : 'Create Training'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SortableThProps {
  col: SortColumn;
  align?: 'left' | 'right';
  sticky?: boolean;
  stickyRight?: boolean;
  emphasize?: boolean;
  title?: string;
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (col: SortColumn) => void;
  children: React.ReactNode;
}

function SortableTh({ col, align = 'left', sticky, stickyRight, emphasize, title, sortColumn, sortDirection, onSort, children }: SortableThProps) {
  const isActive = sortColumn === col;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  const stickyClass = sticky
    ? 'sticky left-0 z-10 bg-gray-50'
    : stickyRight
      ? 'sticky right-0 z-10 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]'
      : '';
  const weightClass = emphasize ? 'font-semibold text-gray-900' : 'font-medium text-gray-700';
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 ${alignClass} ${stickyClass} ${weightClass} cursor-pointer select-none hover:bg-gray-100`}
      title={title}
      onClick={() => onSort(col)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {children}
        {isActive ? (
          sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <span className="inline-block h-3 w-3 opacity-0" aria-hidden="true" />
        )}
      </span>
    </th>
  );
}
